import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, PopoverDialog, SpinningLoader, styles, usePopoverDialog } from '@frontend/design-system';
import { Palette } from './Palette';

interface Props {
  selectedColor: string;
  palette: string[];
  onChange: (color: string) => void;
  gridColumns?: number;
  closeOnSelect?: boolean;
  children?: React.ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  showToggle?: boolean;
}

export const ColorPickerModal = ({
  selectedColor,
  palette,
  onChange,
  gridColumns = 5,
  closeOnSelect = false,
  children,
  isLoading = false,
  disabled = false,
  showToggle = true,
}: Props) => {
  const { t } = useTranslation('schedule');
  const {
    getTriggerProps,
    getDialogProps,
    isOpen,
    close: closeColorPicker,
  } = usePopoverDialog({ placement: 'bottom-start' });

  return (
    <>
      <NakedButton
        css={[styles.flexCenter, buttonStyle(showToggle)]}
        {...getTriggerProps()}
        aria-label={t('Select color')}
        disabled={disabled}
      >
        {isLoading ? <SpinningLoader size='xxs' /> : <div css={colorCircleStyle(selectedColor)} />}
        {showToggle && (
          <span css={[styles.flexCenter, iconContainerStyle]}>
            <Icon name='alt-caret-down-tiny' color='subdued' size={8} css={iconTransformStyle(isOpen)} />
          </span>
        )}
      </NakedButton>
      <PopoverDialog css={popoverDialogStyle} {...getDialogProps()}>
        <Palette
          selectedColor={selectedColor}
          onChange={onChange}
          close={closeOnSelect ? closeColorPicker : undefined}
          palette={palette}
          gridColumns={gridColumns}
        />
        {children}
      </PopoverDialog>
    </>
  );
};

const buttonStyle = (showToggle = true) =>
  css({
    position: 'relative',
    height: 40,
    gap: theme.spacing(0.5),
    border: `1px solid ${theme.colors.neutral30}`,
    borderRadius: theme.borderRadius.small,
    padding: theme.spacing(1.5, showToggle ? 1 : 1.5, 1.5, 1.5),
    ':hover': {
      '> span': {
        borderColor: theme.colors.neutral40,
      },
    },
    ':disabled': {
      cursor: 'default',
      opacity: 0.5,
    },
  });

const iconContainerStyle = css({
  width: 16,
  height: 16,
  borderRadius: theme.borderRadius.small,
  transition: 'background 150ms ease-out',
});

const colorCircleStyle = (color: string) =>
  css({
    minWidth: 20,
    minHeight: 20,
    borderRadius: theme.borderRadius.full,
    backgroundColor: color,
    width: 20,
    height: 20,
    border: `1px solid ${theme.colors.neutral30}`,
  });

const iconTransformStyle = (isOpen: boolean) =>
  css({
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.25s ease-out',
  });

const popoverDialogStyle = css({
  borderRadius: `${theme.borderRadius.medium}`,
  padding: theme.spacing(1),
});
